import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import moment from 'moment'
import useNetworkHeader from '../../../hooks/useNetworkHeader'
import useSubgraphs from '../../../hooks/useSubgraphs'
import useCrons from '../../../hooks/useCrons'
import { formatDuration } from '../../../hooks/useCrons'
import { CRON_NAMES } from '../../../constants/networks'
import { CronStatusResponse } from '../../../types/API'

interface Props {
  envId: string
  chainId: number
}

export default function ChainHeader({ envId, chainId }: Props) {
  const network = useNetworkHeader(envId, String(chainId))
  const subgraphs = useSubgraphs()
  const { data: crons, isLoading } = useCrons()

  let cronName = CRON_NAMES[chainId] || ''
  let cronSelection = crons?.filter((o) => o.name.toLowerCase()?.includes(cronName))

  function formatName(itemName: string, cronName: string) {
    const replaced = itemName.replace(cronName, '').replace(/-+$/, '')
    return replaced.replace(/-/g, '_').replace(/_./g, (m) => m.toUpperCase()[1])
  }

  useEffect(() => {
    cronName = CRON_NAMES[chainId] || ''
    cronSelection = crons?.filter((o) => o.name.toLowerCase()?.includes(cronName))
  }, [chainId])

  const subgraph = subgraphs?.data?.filter((item) => item.chainId === chainId.toString())

  return (
    <Wrap>
      <SectionTitle>Chain</SectionTitle>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Chain</Th>
              <Th>Denied</Th>
              <Th>TX Delay</Th>
              <Th>TX Expirations</Th>
              <Th>Missing TX Timeout</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td>{chainId}</Td>
              <Td>
                {network?.data?.denied === false && <StatusBadge ok>Not denied</StatusBadge>}
                {network?.data?.denied === true && <StatusBadge ok={false}>Denied</StatusBadge>}
              </Td>
              <Td>{network?.data && <Mono>{network.data.transactionDelaySeconds} sec</Mono>}</Td>
              <Td>{network?.data && <Mono>{network.data.transactionsExpirationSeconds || '—'} sec</Mono>}</Td>
              <Td>{network?.data && <Mono>{network.data.missingTxMinutesTimeout || '—'} min</Mono>}</Td>
            </tr>
          </tbody>
        </Table>
      </TableWrap>

      <SectionTitle>Subgraph</SectionTitle>
      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>URL</Th>
              <Th>Subgraph</Th>
              <Th>Synced</Th>
              <Th>Fatal error</Th>
              <Th>Non-fatal errors</Th>
            </tr>
          </thead>
          <tbody>
            {subgraph?.map((item, index) => (
              <tr key={index}>
                <Td><SubUrl>{network?.data?.subgraphUrl}</SubUrl></Td>
                <Td>{item.subgraph}</Td>
                <Td>
                  <StatusDot ok={item.synced} title={item.synced ? 'Synced' : 'Not synced'} />
                </Td>
                <Td>
                  {item.fatalError
                    ? <ErrorText>{item.fatalError.message}</ErrorText>
                    : <Muted>—</Muted>}
                </Td>
                <Td>
                  {item.nonFatalErrors?.length > 0
                    ? item.nonFatalErrors.map(({ message }: any, key: number) => (
                        <ErrorText key={key}>{message}</ErrorText>
                      ))
                    : <Muted>—</Muted>}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>

      {!isLoading && cronSelection && cronSelection.length > 0 && (
        <>
          <SectionTitle>Cron</SectionTitle>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Duration</Th>
                  <Th>Last start</Th>
                  <Th>Last completion</Th>
                  <Th>Status</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {cronSelection.map((item, index) => {
                  const duration = moment.duration(moment(item.lastCompletionTime).diff(moment(item.lastStartTime)))
                  const isStale = duration.asMinutes() < 2 && item.active !== true
                  return (
                    <tr key={index}>
                      <Td><CronName>{item.name}</CronName></Td>
                      <Td>
                        <Mono>{formatDuration(duration)}</Mono>
                        {isStale && <WarnBadge>stale</WarnBadge>}
                      </Td>
                      <Td><Mono>{moment(item.lastStartTime).format('DD/MM/YY HH:mm')}</Mono></Td>
                      <Td>
                        {item.active === true
                          ? <RunningBadge>Running</RunningBadge>
                          : <Mono>{moment(item.lastCompletionTime).format('DD/MM/YY HH:mm')}</Mono>}
                      </Td>
                      <Td>
                        <StatusDot ok={item.active !== true && !isStale} title={item.active ? 'Running' : isStale ? 'Stale' : 'OK'} />
                      </Td>
                      <Td>
                        <LogsLink
                          to={'./dashboard/settings/logs/groups?name=' + formatName(item.name, cronName)}
                          target="_blank"
                        >
                          Logs →
                        </LogsLink>
                      </Td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </TableWrap>
        </>
      )}
    </Wrap>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SectionTitle = styled.h2`
  color: #777;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin: 0;
`

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`

const Th = styled.th`
  padding: 10px 12px;
  text-align: left;
  color: #a1a1a1;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`

const Td = styled.td`
  padding: 10px 12px;
  color: #a0a0a0;
  font-size: 13px;
  vertical-align: middle;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  tr:last-child & { border-bottom: none; }
`

const Mono = styled.span`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 12px;
  color: #a0a0a0;
`

const Muted = styled.span`color: #777;`

const SubUrl = styled.span`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 11px;
  color: #777;
  word-break: break-all;
`

const CronName = styled.span`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 12px;
  color: #ededed;
`

const StatusBadge = styled.span<{ ok: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${({ ok }) => ok ? 'rgba(51,194,176,0.1)' : 'rgba(255,68,68,0.1)'};
  color: ${({ ok }) => ok ? '#33C2B0' : '#ff4444'};
  border: 1px solid ${({ ok }) => ok ? 'rgba(51,194,176,0.25)' : 'rgba(255,68,68,0.25)'};
`

const StatusDot = styled.span<{ ok: boolean }>`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ ok }) => ok ? '#33C2B0' : '#ff4444'};
`

const WarnBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  background: rgba(255,204,51,0.1);
  color: #FFCC33;
  border: 1px solid rgba(255,204,51,0.25);
  margin-left: 6px;
`

const RunningBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  background: rgba(111,92,230,0.1);
  color: #a89aff;
  border: 1px solid rgba(111,92,230,0.25);
`

const ErrorText = styled.p`
  color: #ff4444;
  font-size: 11px;
  margin: 2px 0;
`

const LogsLink = styled(Link)`
  color: #777;
  font-size: 12px;
  text-decoration: none;
  transition: color 0.15s;
  &:hover { color: #a89aff; }
`
